import { useState } from "react";

import { ToggleInput, useToast } from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import {
  usePaymentGatewayConnectionQuery,
  useUpdatePaymentGatewayActiveMutation,
} from "@/modules/settings/services/usePaymentGatewayConnection";
import { IntegrationTile } from "./IntegrationTile";
import { PaymentGatewaySheet } from "./PaymentGatewaySheet";
import { GatewayLogo } from "./logos/GatewayLogo";
import type { GatewayDescriptor } from "./paymentGatewayDescriptors";

type Props = {
  storeSlug: string;
  descriptor: GatewayDescriptor;
};

export function PaymentGatewayTile({ storeSlug, descriptor }: Props) {
  const t = useTranslations();
  const toast = useToast();
  const { connection, isLoading } = usePaymentGatewayConnectionQuery({
    storeSlug,
    provider: descriptor.provider,
  });
  const { mutateAsync: updateActive, isPending: isToggling } =
    useUpdatePaymentGatewayActiveMutation(descriptor.provider);
  const [sheetOpen, setSheetOpen] = useState(false);

  const status = isLoading
    ? "loading"
    : !connection
      ? "disconnected"
      : connection.isActive
        ? "connected"
        : "paused";

  const statusLabel = !connection
    ? t(keys.paymentGateway.statusNotConnected)
    : connection.isActive
      ? t(keys.paymentGateway.statusConnected)
      : t(keys.paymentGateway.statusPaused);

  const handleToggle = async (next: boolean) => {
    try {
      await updateActive({ storeSlug, isActive: next });
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t(keys.errors.generic),
      );
    }
  };

  const footer = connection ? (
    <>
      <span className="text-xs text-[color:var(--text-secondary)]">
        {connection.isActive
          ? t(keys.paymentGateway.pauseAction)
          : t(keys.paymentGateway.resumeAction)}
      </span>
      <ToggleInput
        name={`${descriptor.provider}-active`}
        checked={connection.isActive}
        disabled={isToggling}
        onChange={(event) => handleToggle(event.target.checked)}
        ariaLabel={
          connection.isActive
            ? t(keys.paymentGateway.pauseAction)
            : t(keys.paymentGateway.resumeAction)
        }
      />
    </>
  ) : undefined;

  return (
    <>
      <IntegrationTile
        name={t(descriptor.brandKey)}
        description={t(descriptor.descKey)}
        logo={<GatewayLogo descriptor={descriptor} />}
        status={status}
        statusLabel={statusLabel}
        onClick={() => setSheetOpen(true)}
        footer={footer}
      />
      <PaymentGatewaySheet
        storeSlug={storeSlug}
        descriptor={descriptor}
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
      />
    </>
  );
}
